import { useState, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { schema } from './schema';
import { PLUGIN_DESTINATIONS } from '../../../constants';
import {
  isSolrCloud,
  isVersionCompatible,
  sentenceCase,
} from '../../../utils/utils';
import Loading from '../../elements/loading/Loading';
import Text from '../../elements/text/Text';

const PluginForm = ({
  extensionType,
  version,
  handleAdd,
  getInstances,
  getInstanceNodes,
  getCollections,
  getLibPath,
}) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [instances, setInstances] = useState(null);
  const [nodes, setNodes] = useState(null);
  const [collections, setCollections] = useState(null);
  const [path, setPath] = useState(null);
  const [versionWarning, setVersionWarning] = useState('');

  const {
    handleSubmit,
    register,
    watch,
    control,
    setValue,
    // reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema()),
    defaultValues: { extensionType, version },
  });

  const { instance, downloadTo, collectionName } = watch();

  const getCollectionData = async () => {
    setIsLoading(true);
    try {
      const data = await getCollections(instance);
      setCollections(data);
    } catch (error) {
      setCollections(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getInstanceNodeData = async () => {
    setIsLoading(true);
    try {
      const data = await getInstanceNodes(instance.id);
      setNodes(data);
    } catch (error) {
      setNodes(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const getInstanceData = async () => {
      try {
        const data = await getInstances();
        setInstances(data);
      } catch (error) {
        setInstances(null);
      }
    };

    getInstanceData();
  }, []);

  useEffect(() => {
    if (!instance) return;

    // const extensionType = getValues('extensionType');
    const version = getValues('version');

    // check version difference
    const isCompatible = isVersionCompatible(
      version.supportedSolrVersions,
      instance.solrVersion
    );

    setVersionWarning(
      isCompatible
        ? ''
        : t('downloadVersionDiffWarning', {
            ns: 'descriptions',
            origin: version.supportedSolrVersions.join(', '),
            target: instance.solrVersion,
          })
    );

    // reset when instance is changed
    // setNodes(null);
    // reset({ extensionType, version });

    // fetch instance nodes
    if (isSolrCloud(instance?.type)) {
      getInstanceNodeData();
    }
  }, [instance]);

  useEffect(() => {
    if (downloadTo) {
      getCollectionData();
      setValue('collectionName', '');
    }
  }, [downloadTo, instance]);

  useEffect(() => {
    if (isEmpty(nodes)) return;
    const nodeIds = nodes.map((node) => node.id);
    setValue('instanceNodes', nodeIds);
  }, [nodes]);

  useEffect(() => {
    setPath(null);
    if (!downloadTo && !nodes) return;
    const collectionNameNeeded = downloadTo === 'collection';

    const getPath = () => {
      const path = getLibPath(instance, downloadTo, collectionName);
      setPath(path);
      setValue('path', path);
    };

    if (collectionNameNeeded) {
      collectionName && getPath();
    } else {
      getPath();
    }
  }, [downloadTo, nodes, collectionName]);

  const onSubmit = (data) => {
    setIsProcessing(true);
    handleAdd(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} aria-label="plugin extension form">
      <Grid container spacing={2}>
        {versionWarning && (
          <Grid size={12}>
            <Alert severity="warning">{versionWarning}</Alert>
          </Grid>
        )}
        <Grid size={12}>
          <FormControl fullWidth error={!!errors.instance}>
            <InputLabel>
              {t('ext.instanceToDownload', { ns: 'project' })}
            </InputLabel>
            <Select
              {...register('instance')}
              label={t('ext.instanceToDownload', { ns: 'project' })}
              defaultValue=""
              inputProps={{ 'aria-label': 'instance select' }}
            >
              {isEmpty(instances) ? (
                <MenuItem value="">
                  {t('noInstances', { ns: 'instance' })}
                </MenuItem>
              ) : (
                instances?.map((instance) => (
                  <MenuItem key={instance.id} value={instance}>
                    {instance.name}
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>{errors.instance?.message}</FormHelperText>
          </FormControl>
        </Grid>
        {instance && (
          <Grid size={12}>
            {isSolrCloud(instance?.type) ? (
              <>
                <Text>
                  {t('pluginDefaultDestinationSolrCloud', {
                    ns: 'descriptions',
                  })}
                </Text>
                {!isLoading && nodes ? (
                  <FormControl fullWidth error={!!errors.instanceNodes}>
                    <Controller
                      name="instanceNodes"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Select
                            {...field}
                            multiple
                            value={field.value ?? []}
                            displayEmpty
                            label={t('nodeSelect', { ns: 'fields' })}
                            inputProps={{
                              'aria-label': 'nodes multiple select',
                            }}
                            renderValue={(selected) => {
                              if (!selected) return;
                              if (selected.length === 0)
                                return sentenceCase(
                                  t('selectRequired', {
                                    ns: 'validations',
                                    field: t('instanceNode', {
                                      ns: 'instance',
                                    }),
                                  })
                                );
                              const selectedNodes = selected.map(
                                (item) =>
                                  nodes.find((node) => node.id === item).name
                              );
                              return selectedNodes.join(', ');
                            }}
                          >
                            {nodes?.map((item) => (
                              <MenuItem key={item.id} value={item.id}>
                                <Checkbox
                                  checked={field.value?.includes(item.id)}
                                  inputProps={{
                                    'aria-label': 'select checkbox',
                                  }}
                                />
                                <ListItemText primary={item.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        );
                      }}
                    />
                    <FormHelperText>
                      {errors.instanceNodes?.message}
                    </FormHelperText>
                  </FormControl>
                ) : (
                  <Loading />
                )}
              </>
            ) : (
              <>
                <Text>
                  {t('pluginDefaultDestinationStandalone', {
                    ns: 'descriptions',
                  })}
                </Text>
                <FormControl fullWidth error={!!errors.downloadTo}>
                  <Controller
                    name="downloadTo"
                    control={control}
                    defaultValue={''}
                    render={({ field }) => (
                      <RadioGroup {...field} value={field.value || ''}>
                        <FormHelperText>
                          {errors.downloadTo?.message}
                        </FormHelperText>
                        {PLUGIN_DESTINATIONS.map((item) => (
                          <FormControlLabel
                            key={item.value}
                            value={item.value}
                            control={<Radio />}
                            label={t(item.label, { ns: 'menus' })}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </FormControl>
                {downloadTo === 'collection' &&
                  (isLoading ? (
                    <Loading />
                  ) : (
                    <FormControl fullWidth error={!!errors.collectionName}>
                      <InputLabel>
                        {t('ext.collectionToDownload', { ns: 'project' })}
                      </InputLabel>
                      <Select
                        {...register('collectionName')}
                        label={t('ext.collectionToDownload', { ns: 'project' })}
                        defaultValue=""
                        inputProps={{ 'aria-label': 'collection select' }}
                      >
                        {isEmpty(collections) ? (
                          <MenuItem value="">
                            {t('noCollections', { ns: 'instance' })}
                          </MenuItem>
                        ) : (
                          collections?.map((collection) => (
                            <MenuItem key={collection} value={collection}>
                              {collection}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      <FormHelperText>
                        {errors.collectionName?.message}
                      </FormHelperText>
                    </FormControl>
                  ))}
              </>
            )}
          </Grid>
        )}
      </Grid>
      <Box sx={{ my: 2, textAlign: 'center' }}>
        {path && (
          <Box sx={{ my: 1, textAlign: 'center' }}>
            <Text color="primary">
              {t('ext.downloadPath', { ns: 'project', path })}
            </Text>
          </Box>
        )}
        <Button
          type="submit"
          disabled={!isEmpty(errors) || isProcessing}
          aria-label="confirm"
        >
          {t('download', { ns: 'buttons' })}
        </Button>
      </Box>
    </form>
  );
};

PluginForm.propTypes = {
  extensionType: PropTypes.string.isRequired,
  version: PropTypes.object.isRequired,
  handleAdd: PropTypes.func.isRequired,
  getInstances: PropTypes.func.isRequired,
  getInstanceNodes: PropTypes.func.isRequired,
  getCollections: PropTypes.func.isRequired,
  getLibPath: PropTypes.func.isRequired,
};

export default PluginForm;
